// src/service/socketIoManager.js
import { io } from "socket.io-client";
import { store } from "../Redux/Store";
import { setTasks } from "../Redux/service/Websocket";
import { setIREvent } from "../Redux/service/IR/IRWebSocket";
import { setSnagEvent } from "../Redux/service/Snag/SnagWebSocket";

const getBackendOrigin = (url = "") => {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
};

const BACKEND_ORIGIN = getBackendOrigin(process.env.REACT_APP_BACKEND_URL);
const BACKEND2_ORIGIN = getBackendOrigin(process.env.REACT_APP_BACKEND2_URL);

class SocketIOManager {
  constructor() {
    this.socketMain = null;
    this.socketTest = null;
    this.isConnectedMain = false;
    this.isConnectedTest = false;
    this.currentUserId = null;
    this.currentUserRole = null;
    this.joinedRooms = new Set();
  }

  connectMain(authToken) {
    if (this.socketMain) {
      this.disconnectMain();
    }

    this.socketMain = io("https://dev.softsprint.ae", {
      path: "/MaintBackend/socket.io/",
      auth: { token: authToken },
      transports: ["websocket"],
    });

    this.socketMain.on("connect", () => {
      this.isConnectedMain = true;
      console.log("✅ Socket.IO connected to MaintBackend");
    });

    this.socketMain.on("task:updated", (updatedTaskData) => {
      console.log("📥 [MaintBackend] Task updated:", updatedTaskData);
      store.dispatch(setTasks(updatedTaskData));
    });

    this.socketMain.on("task:created", (newTaskData) => {
      console.log("📥 [MaintBackend] Task created:", newTaskData);
      store.dispatch(setTasks(newTaskData));
    });

    this.socketMain.on("task:deleted", (deletedTaskData) => {
      console.log("📥 [MaintBackend] Task deleted:", deletedTaskData);
      store.dispatch(setTasks(deletedTaskData));
    });

    this.socketMain.on("disconnect", (reason) => {
      this.isConnectedMain = false;
      console.log("❌ MaintBackend Socket.IO disconnected:", reason);
    });

    this.socketMain.on("connect_error", (error) => {
      console.error("❌ MaintBackend connection error:", error.message);
    });
  }

  connectTest(authToken, userInfo) {
    if (this.socketTest) {
      this.disconnectTest();
    }

    // ✅ Extract user info
    this.currentUserId = userInfo?.id;
    this.currentUserRole = userInfo?.attributes?.role?.toLowerCase();

    this.socketTest = io("https://dev.softsprint.ae", {
      path: "/backendTest/socket.io/",
      auth: { token: authToken },
      transports: ["websocket"],
    });

    this.socketTest.on("connect", () => {
      this.isConnectedTest = true;
      console.log("✅ Socket.IO connected to backendTest");

      // ✅ JOIN ROOMS after connection
      this.joinUserRooms();
    });

    // Task/Session events
    this.socketTest.on("task:session_started", (data) => {
      console.log("📥 [backendTest] Session started:", data);
      store.dispatch(setTasks(data));
    });

    this.socketTest.on("task:session_ended", (data) => {
      console.log("📥 [backendTest] Session ended:", data);
      store.dispatch(setTasks(data));
    });

    this.socketTest.on("task:session_submitted", (data) => {
      console.log("📥 [backendTest] Session submitted:", data);
      store.dispatch(setTasks(data));
    });

    // ============= IR EVENT LISTENERS =============
    const invalidateIRCache = async (tags) => {
      try {
        const { IRAPI } = await import("../Redux/service/IR/IR");
        store.dispatch(IRAPI.util.invalidateTags(tags));
      } catch (error) {
        console.error("Error invalidating IR cache:", error);
      }
    };

    this.socketTest.on("ir:created", (data) => {
      console.log("📥 [backendTest] IR created:", data);
      store.dispatch(setIREvent({ data, eventType: "created" }));
      invalidateIRCache([{ type: "IRList", id: "LIST" }, "Statistics"]);
    });

    this.socketTest.on("ir:updated", (data) => {
      console.log("📥 [backendTest] IR updated:", data);
      store.dispatch(setIREvent({ data, eventType: "updated" }));
      invalidateIRCache([{ type: "IR", id: data.id }, { type: "IRList", id: "LIST" }]);
    });

    this.socketTest.on("ir:resubmitted", (data) => {
      console.log("📥 [backendTest] IR resubmitted:", data);
      store.dispatch(setIREvent({ data, eventType: "resubmitted" }));
      invalidateIRCache([
        { type: "IR", id: data.id },
        { type: "IRList", id: "LIST" },
        "PendingReviews",
      ]);
    });

    this.socketTest.on("ir:qa_reviewed", (data) => {
      console.log("📥 [backendTest] IR QA reviewed:", data);
      store.dispatch(setIREvent({ data, eventType: "qa_reviewed" }));
      invalidateIRCache([
        { type: "IR", id: data.id },
        { type: "IRList", id: "LIST" },
        "PendingReviews",
        "Statistics",
      ]);
    });

    this.socketTest.on("ir:consultant_reviewed", (data) => {
      console.log("📥 [backendTest] IR consultant reviewed:", data);
      store.dispatch(setIREvent({ data, eventType: "consultant_reviewed" }));
      invalidateIRCache([
        { type: "IR", id: data.id },
        { type: "IRList", id: "LIST" },
        "PendingReviews",
        "Statistics",
      ]);
    });

    // ============= SNAG EVENT LISTENERS =============
    const invalidateSnagCache = async (tags) => {
      try {
        const { SnagAPI } = await import("../Redux/service/Snag/Snag");
        store.dispatch(SnagAPI.util.invalidateTags(tags));
      } catch (error) {
        console.error("Error invalidating Snag cache:", error);
      }
    };

    // Snag Created
    this.socketTest.on("snag:created", (data) => {
      console.log("📥 [backendTest] Snag created:", data);
      store.dispatch(setSnagEvent({ data, eventType: "created" }));
      invalidateSnagCache([{ type: "SnagList", id: "LIST" }, "Statistics"]);
    });

    // Snag Updated
    this.socketTest.on("snag:updated", (data) => {
      console.log("📥 [backendTest] Snag updated:", data);
      store.dispatch(setSnagEvent({ data, eventType: "updated" }));
      invalidateSnagCache([{ type: "Snag", id: data.id }, { type: "SnagList", id: "LIST" }]);
    });

    // Status changed listeners
    this.socketTest.on("snag:status_changed_fix", (data) => {
      console.log("📥 [backendTest] Snag fixed:", data);
      store.dispatch(setSnagEvent({ data, eventType: "fixed" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
      ]);
    });

    this.socketTest.on("snag:status_changed_rectify", (data) => {
      console.log("📥 [backendTest] Snag rectified:", data);
      store.dispatch(setSnagEvent({ data, eventType: "rectified" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
      ]);
    });

    this.socketTest.on("snag:status_changed_verify", (data) => {
      console.log("📥 [backendTest] Snag verified:", data);
      store.dispatch(setSnagEvent({ data, eventType: "verified" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
        "Statistics",
      ]);
    });

    this.socketTest.on("snag:status_changed_closed", (data) => {
      console.log("📥 [backendTest] Snag closed:", data);
      store.dispatch(setSnagEvent({ data, eventType: "closed" }));
      invalidateSnagCache([{ type: "Snag", id: data.id }, { type: "SnagList", id: "LIST" }, "Statistics"]);
    });

    this.socketTest.on("snag:status_changed_reopen", (data) => {
      console.log("📥 [backendTest] Snag reopened:", data);
      store.dispatch(setSnagEvent({ data, eventType: "reopened" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
      ]);
    });

    this.socketTest.on("snag:status_changed_in_dispute", (data) => {
      console.log("📥 [backendTest] Snag disputed:", data);
      store.dispatch(setSnagEvent({ data, eventType: "disputed" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
      ]);
    });

    this.socketTest.on("snag:qa_reviewed", (data) => {
      console.log("📥 [backendTest] Snag QA reviewed:", data);
      store.dispatch(setSnagEvent({ data, eventType: "qa_reviewed" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
        "Statistics",
      ]);
    });

    this.socketTest.on("snag:consultant_reviewed", (data) => {
      console.log("📥 [backendTest] Snag consultant reviewed:", data);
      store.dispatch(setSnagEvent({ data, eventType: "consultant_reviewed" }));
      invalidateSnagCache([
        { type: "Snag", id: data.id },
        { type: "SnagList", id: "LIST" },
        "PendingReviews",
        "Statistics",
      ]);
    });

    // ============= NOTIFICATION EVENT LISTENER =============
    // ✅ SINGLE listener (not nested), and DOES NOT read data.additionalData
    this.socketTest.on("notification", (data) => {
      console.log("📥 [backendTest] Notification received:", data);
      console.log("📥 Notification type:", data.type);

      const snagNotificationTypes = [
        "snag_assigned",
        "snag_created",
        "snag_updated",
        "snag_fixed",
        "snag_rectified",
        "snag_verified",
        "snag_closed",
        "snag_reopened",
        "snag_disputed",
      ];

      if (!snagNotificationTypes.includes(data.type)) {
        console.log("ℹ️ Ignoring non-snag notification:", data.type);
        return;
      }

      // Map notification type to eventType
      let eventType = "created";
      if (data.type === "snag_assigned" || data.type === "snag_created") eventType = "created";
      else if (data.type === "snag_updated") eventType = "updated";
      else if (data.type === "snag_fixed") eventType = "fixed";
      else if (data.type === "snag_rectified") eventType = "rectified";
      else if (data.type === "snag_verified") eventType = "verified";
      else if (data.type === "snag_closed") eventType = "closed";
      else if (data.type === "snag_reopened") eventType = "reopened";
      else if (data.type === "snag_disputed") eventType = "disputed";

      // ✅ Backend spreads additionalData at root (notificationService socketPayload uses ...additionalData)
      const snagData = {
        id: data.snag_id || data.related_id || data.id,
        snag_number: data.snag_number || data.title?.match(/SNAG-\w+-\d+/)?.[0],
        severity: data.severity,
        snag_type: data.snag_type,
        status: data.status || "OPEN",

        // normalize project
        project: data.project || { id: data.project_id, name: data.project_name },

        // normalize reporter
        reported_by_name:
          data.reported_by_name ||
          data.reportedBy?.name ||
          data.submittedBy?.name ||
          (typeof data.reportedBy === "string" ? data.reportedBy : "") ||
          (typeof data.submittedBy === "string" ? data.submittedBy : ""),

        // normalize team
        assignedTeam: {
          id: data.team_id,
          name: data.team_name,
        },

        title: data.title,
        message: data.message,
      };

      console.log("📥 Transformed snagData:", snagData);

      store.dispatch(setSnagEvent({ data: snagData, eventType }));
      invalidateSnagCache([{ type: "SnagList", id: "LIST" }, "Statistics"]);
    });

    // ============= DISCONNECT HANDLERS =============
    this.socketTest.on("disconnect", (reason) => {
      this.isConnectedTest = false;
      this.joinedRooms.clear();
      console.log("❌ backendTest Socket.IO disconnected:", reason);
    });

    this.socketTest.on("connect_error", (error) => {
      console.error("❌ backendTest connection error:", error.message);
    });
  }

  // ✅ Join user-specific room
  joinUserRooms() {
    if (!this.socketTest || !this.isConnectedTest) {
      console.warn("⚠️ Socket not connected, cannot join rooms");
      return;
    }

    if (this.currentUserId) {
      const userRoom = `user:${this.currentUserId}`;
      this.socketTest.emit("join_room", userRoom);
      this.joinedRooms.add(userRoom);
      console.log(`✅ Joined room: ${userRoom}`);
    }
  }

  // ✅ Join project-specific rooms
  joinProjectRooms(projectIds) {
    if (!this.socketTest || !this.isConnectedTest) {
      console.warn("⚠️ Socket not connected, cannot join project rooms");
      return;
    }

    if (!this.currentUserRole) {
      console.warn("⚠️ User role not set, cannot join project rooms");
      return;
    }

    projectIds.forEach((projectId) => {
      const projectRoom = `project:${projectId}:${this.currentUserRole}`;
      this.socketTest.emit("join_room", projectRoom);
      this.joinedRooms.add(projectRoom);
      console.log(`✅ Joined room: ${projectRoom}`);
    });
  }

  updateProjectRooms(projectIds) {
    if (!this.socketTest || !this.isConnectedTest) return;

    // Leave old project rooms
    const oldProjectRooms = Array.from(this.joinedRooms).filter((room) =>
      room.startsWith("project:")
    );

    oldProjectRooms.forEach((room) => {
      this.socketTest.emit("leave_room", room);
      this.joinedRooms.delete(room);
      console.log(`🚪 Left room: ${room}`);
    });

    // Join new project rooms
    this.joinProjectRooms(projectIds);
  }

  disconnectMain() {
    if (this.socketMain) {
      this.socketMain.disconnect();
      this.socketMain = null;
      this.isConnectedMain = false;
      console.log("ℹ️ MaintBackend Socket.IO manually disconnected");
    }
  }

  disconnectTest() {
    if (this.socketTest) {
      this.socketTest.disconnect();
      this.socketTest = null;
      this.isConnectedTest = false;
      this.joinedRooms.clear();
      this.currentUserId = null;
      this.currentUserRole = null;
      console.log("ℹ️ backendTest Socket.IO manually disconnected");
    }
  }

  getIsConnectedMain() {
    return this.isConnectedMain;
  }

  getIsConnectedTest() {
    return this.isConnectedTest;
  }

  getJoinedRooms() {
    return Array.from(this.joinedRooms);
  }
}

export const socketIOManager = new SocketIOManager();
